import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import type {IDivisionsStore} from "data/stores/divisions/divisions.store";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";
import type {
	IDivisionSlot,
	ILightPredictionsStore,
} from "data/stores/light_predictions/light_predictions.store";
import {ConferenceType, GameSteps} from "data/enums";
import {get} from "lodash";
import type {IConferencesStore} from "data/stores/conferences/conferences.store";
import {AFC_PICK_STEPS, NFC_PICK_STEPS} from "data/constants";
import type {IStanding, IStandingsStore} from "data/stores/standings/standings.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IUserStore} from "data/stores/user/user.store";

interface IWildCardSquad extends ISquad {
	isSelected: boolean;
	isDisabled: boolean;
	slotIndex: number;
}

interface IProps {
	step: GameSteps;
}

export interface ILPWildCardController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get isShowNextButton(): boolean;
	get isDisableNextButton(): boolean;
	get selectedConferenceBg(): string;
	get divisionName(): string;
	get squads(): IWildCardSquad[];
	get predictions(): IDivisionSlot[];
	get predictionsFull(): boolean;

	onSelectSquad: (squad: IWildCardSquad) => void;
	toNextStep: () => void;
	squadStanding: (squadId: number) => IStanding | undefined;
}

@injectable()
export class LPWildCardController implements ILPWildCardController {
	@observable private _step: GameSteps = GameSteps.NFC_WILD_CARD;

	get isShowNextButton() {
		return this.conferenceType === ConferenceType.NFC;
	}

	get wildCardPredictions() {
		return this.predictions.filter(({type}) =>
			[GameSteps.NFC_WILD_CARD, GameSteps.AFC_WILD_CARD].includes(type)
		);
	}

	get isDisableNextButton() {
		return !this.predictionsFull;
	}

	get conferenceType() {
		return this._lpStore.getConferenceByType(this._step);
	}

	get selectedConferenceBg() {
		return this.conferenceType === ConferenceType.NFC ? "#103365" : "#d50a0a";
	}

	get currentDivision() {
		return this._divisionStore.divisions.find(({abbreviation}) => abbreviation === this._step);
	}

	get divisionName() {
		return get(this.currentDivision, "name", "");
	}

	get squads() {
		const predictions = this.predictions.filter((prediction) =>
			[...NFC_PICK_STEPS, ...AFC_PICK_STEPS].includes(prediction.type)
		);

		const conferenceId = this._conferenceStore.getConferenceIdByType(this.conferenceType);

		return this._squadStore.list
			.filter((squad) => squad.conferenceId === conferenceId)
			.filter(({id}) => !predictions.find((prediction) => prediction.squadId === id))
			.map((squad) => {
				const prediction = this.predictions.find(({squadId}) => squadId === squad.id);
				const isSelected = Boolean(prediction);
				const isDisabled = !isSelected && this.predictionsFull;

				return {
					...squad,
					isSelected,
					isDisabled,
					slotIndex: prediction?.position || 0,
				};
			});
	}

	get predictions() {
		return this._lpStore.lightPrediction[this.conferenceType];
	}

	get predictionsFull() {
		return this.wildCardPredictions.every(({squadId}) => squadId);
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ConferencesStore) private _conferenceStore: IConferencesStore,
		@inject(Bindings.DivisionsStore) private _divisionStore: IDivisionsStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	init(param: IProps) {
		this._step = param.step;
	}

	onSelectSquad = (squad: IWildCardSquad) => {
		if (squad.isSelected) {
			const prediction = this.predictions.find(({squadId}) => squadId === squad.id)!;
			prediction.squadId = 0;
		} else {
			const prediction = this.predictions.find(
				({type, squadId}) =>
					[GameSteps.NFC_WILD_CARD, GameSteps.AFC_WILD_CARD].includes(type) && !squadId
			)!;
			prediction.squadId = squad.id;
		}

		this._lpStore.setIsPredictionsSaved(false);
		this._lpStore.setIsPredictionsChanged(true);
	};

	toNextStep = () => {
		if (this.conferenceType === ConferenceType.NFC) {
			this._lpStore.setCurrentStep(GameSteps.AFC_EAST);
			this._lpStore.customSteps.nfc.wildCard = true;
		}
	};

	squadStanding = (squadId: number) => {
		return this._standingsStore.lastCompleteStandingsBySquadId(
			squadId,
			this._roundStore.actualStandingsRoundId
		);
	};
}
